import { useEffect, useMemo, useState, type JSX } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    Button,
    emitNotification,
    ButtonGroup,
    ButtonSpec,
    InformationModal,
} from "@impero-com/ui";
import { useForm } from "react-hook-form";

import {
    ControlProgramTagCategoryView,
    ControlProgramTagView,
} from "api/generated/model";
import { Drawer } from "components/drawer";
import IconDelete from "img/delete.svg";

import { ControlProgramTagsQuerySelect } from "../queries/useControlProgramTagsQuery";
import { useDeleteControlProgramTagCategoryMutation } from "../queries/useDeleteControlProgramTagCategoryMutation";

import { ControlProgramTagCategoryForm } from "./ControlProgramTagCategoryForm";
import {
    ControlProgramTagCategoryFormSchema,
    useControlProgramTagCategoryFormSchema,
} from "./useControlProgramTagCategoryFormSchema";
import { CategoryOverview } from "./useControlProgramTagsListTable";
interface DrawerProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    category: CategoryOverview | null;
}
export function EditControlProgramTagCategoryDrawer({
    isOpen,
    setIsOpen,
    category,
    tagCategories,
}: DrawerProps & ControlProgramTagsQuerySelect) {
    const { t } = useTranslation();
    const [showDeleteCategoryConfirmation, setShowDeleteCategoryConfirmation] =
        useState(false);
    const { mutateAsync: deleteTagCategory } =
        useDeleteControlProgramTagCategoryMutation();

    const sortedInitialTags = useMemo(() => {
        if (!category) {
            return [];
        }
        return category.tags.sort((a, b) => a.name.localeCompare(b.name));
    }, [category]);

    const [tags, setTags] =
        useState<ControlProgramTagView[]>(sortedInitialTags);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setTags(sortedInitialTags);
    }, [sortedInitialTags]);

    const existingTagNames = useMemo(() => {
        return tags.map((tag) => tag.name);
    }, [tags]);
    const schema = useControlProgramTagCategoryFormSchema(existingTagNames);
    const form = useForm<ControlProgramTagCategoryFormSchema>({
        resolver: zodResolver(schema),
        mode: "onSubmit",
        defaultValues: {
            newTagName: "",
            tags: tags.map((tag) => ({
                tagId: tag.id,
                tagName: tag.name,
            })),
        },
    });

    const selectedCategory = tagCategories.find(
        (cat) => cat.name === category?.categoryName
    );

    if (!selectedCategory) {
        return null;
    }

    const handleDeleteCategory = async (categoryId: string) => {
        try {
            await deleteTagCategory({ tagCategoryId: categoryId });
            setShowDeleteCategoryConfirmation(false);
            setIsOpen(false);
            form.reset();
            emitNotification(
                "success",
                t(
                    "TagsList_EditCategoryDrawer_Notification_TagCategoryDeleted",
                    {
                        tagCategoryName: selectedCategory.name,
                    }
                )
            );
        } catch (error) {
            console.error("Failed to delete tag category:", error);
            emitNotification(
                "error",
                t(
                    "TagsList_EditCategoryDrawer_Notification_DeleteTagCategoryFailed"
                )
            );
        }
    };

    const onCancel = () => {
        setIsOpen(false);
        form.reset();
    };

    const deleteButton: ButtonSpec[] = [
        {
            id: "delete",
            label: t("TagsList_EditCategoryDrawer_Action_DeleteCategory"),
            type: "link",
            onClick: () => {
                setShowDeleteCategoryConfirmation(true);
            },
            icon: { svg: IconDelete },
        },
    ];

    return (
        <>
            <Drawer
                buildHeader={() =>
                    t("ProgramTagsList_EditCategoryDrawer_Header")
                }
                buildFooter={() => <ButtonGroup buttonSpecs={deleteButton} />}
                onCancel={onCancel}
                open={isOpen}
            >
                <ControlProgramTagCategoryForm
                    form={form}
                    category={selectedCategory}
                    tags={tags}
                    setTags={setTags}
                    existingTagNames={existingTagNames}
                />
            </Drawer>
            <DeleteCategoryConfirmationDialog
                category={selectedCategory}
                show={showDeleteCategoryConfirmation}
                onDeleted={() => void handleDeleteCategory(selectedCategory.id)}
                onCancel={() => setShowDeleteCategoryConfirmation(false)}
            />
        </>
    );
}

interface DeleteCategoryConfirmationDialogProps {
    category: ControlProgramTagCategoryView;
    show: boolean;
    onDeleted: () => void;
    onCancel: () => void;
}
function DeleteCategoryConfirmationDialog({
    category,
    show,
    onDeleted,
    onCancel,
}: DeleteCategoryConfirmationDialogProps): JSX.Element {
    const { t } = useTranslation();

    return (
        <InformationModal
            open={show}
            onClose={() => onCancel()}
            type="error"
            icon="delete"
            title={t(
                "TagsList_EditCategoryDrawer_DeteleCategoryConfirmation_Title"
            )}
            footer={
                <>
                    <Button onClick={() => onCancel()}>{t("Cancel")}</Button>
                    <Button variant="destructive" onClick={() => onDeleted()}>
                        {t("Delete")}
                    </Button>
                </>
            }
        >
            {t("TagsList_EditCategoryDrawer_DeteleCategoryConfirmation_Text", {
                tagCategoryName: category.name,
            })}
        </InformationModal>
    );
}
